import React, { useEffect, useMemo, useState } from "react";
// Work Orders page (Migrated to V2)

// Default
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid2";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import TableSortLabel from "@mui/material/TableSortLabel";

// Custom
import Layout from "../../layout";
import CustomDataTable from "../../components/table/CustomTable";
import CustomToast from "../../components/toast/CustomToast";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatDate, isUUID } from "../../utils/helper";

// Icons
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";

// Svg
import Download from "../../assets/svg/download";
import CustomSearch from "../../components/search/Search";

// Types
import { WorkOrderV2T, WorkOrderResponseV2, ToastState } from "../../types";

const WorkOrders: React.FC = () => {
  const [workOrders, setWorkOrders] = useState<WorkOrderV2T[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("createdAt");

  const handleSort = (key: string) => {
    const isAsc = orderBy === key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(key);
  };

  const calculateTimeTaken = (start: string, end: string | null) => {
    if (!start || !end) return "N/A";
    const startTime = moment(start);
    const endTime = moment(end);
    if (!startTime.isValid() || !endTime.isValid()) return "N/A";

    const duration = moment.duration(endTime.diff(startTime));
    if (duration.asSeconds() < 0) return "0s";

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

    return parts.join(" ");
  };

  const navigate = useNavigate();

  const COLUMNS = [
    { label: "Work Order Number", id: "workOrderNumber" },
    { label: "Meter Number", id: "meterNumber" },
    { label: "Type", id: "workOrderType" },
    { label: "Status", id: "status" },
    { label: "Description", id: "reason" },
    { label: "Requested At", id: "createdAt" },
    { label: "Completed At", id: "completedDate" },
    { label: "Time Taken", id: "timeTaken" },
  ];

  const getStatusDetails = (status: string) => {
    const s = status?.toUpperCase() || "";
    let label = status || "Unknown";
    let color = "#1064ED"; // Default blue
    let bgColor = "#e3f1fa";

    if (s === "COMPLETED" || s === "SUCCESS") {
      color = "#0cb254";
      bgColor = "#e1f5d7";
    } else if (s === "FAILED" || s === "REJECTED") {
      color = "#F65B5B";
      bgColor = "#fff1f1";
    } else if (s === "PENDING" || s === "IN PROGRESS") {
      color = "#FDA81F";
      bgColor = "#fff8e1";
    }

    return { label, color, bgColor };
  };

  const getTypeDetails = (type: string) => {
    const t = type?.toUpperCase() || "";
    let color = "#1064ED";
    let bgColor = "#e3f1fa";

    if (t.includes("DISCONNECT")) {
      color = "#F65B5B";
      bgColor = "#fff1f1";
    } else if (t.includes("CONNECT")) {
      color = "#0cb254";
      bgColor = "#e1f5d7";
    }

    return { label: type, color, bgColor };
  };

  const fetchWorkOrders = async (page: number = 0, pageSize: number = 10) => {
    // If no search term, we can fetch all (optional behaviour, usually V2 needs a search or ID)
    // For this page, searching by meter ID is primary
    if (!searchTerm) {
      setWorkOrders([]);
      setPagination({ ...pagination, totalCount: 0, totalPages: 0 });
      return;
    }

    setIsLoading(true);
    try {
      // V2 API is 1-based, our table is 0-based
      const response = await fetchGETRequest<WorkOrderResponseV2>(
        `v2/work-orders?MeterId=${searchTerm}&Page=${page + 1}&PageSize=${pageSize}`,
        {},
      );

      if (response?.success) {
        const dataWithTimeTaken = (response.data || []).map((item) => {
          let timeTaken = null;
          if (item.createdAt && item.completedDate) {
            timeTaken = moment(item.completedDate).diff(moment(item.createdAt));
          }
          return { ...item, timeTaken };
        });
        setWorkOrders(dataWithTimeTaken);
        setPagination({
          currentPage: page,
          pageSize: pageSize,
          totalCount: response.totalCount || 0,
          totalPages: response.totalPages || 0,
        });
      } else {
        setWorkOrders([]);
      }
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setToast({
        message: "Failed to fetch work orders.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      fetchWorkOrders(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize]);

  const downloadCSV = () => {
    if (workOrders.length === 0) return;

    // Use keys from the first object as headers
    const headers = [
      "WorkOrderNumber",
      "MeterNumber",
      "Type",
      "Status",
      "Description",
      "RequestedAt",
      "CompletedAt",
      "TimeTaken",
    ];
    const rows = workOrders.map((item) => {
      return [
        item.workOrderNumber,
        item.meterNumber,
        item.workOrderType,
        item.status,
        item.reason || "N/A",
        formatDate(item.createdAt, "DD/MM/YYYY HH:mm:ss"),
        item.completedDate
          ? formatDate(item.completedDate, "DD/MM/YYYY HH:mm:ss")
          : "N/A",
        calculateTimeTaken(item.createdAt, item.completedDate),
      ].join(",");
    });

    const csvContent = `${headers.join(",")}\n${rows.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `WorkOrders_${searchTerm || "All"}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    setAnchorEl(null);
  };

  const downloadExcel = () => {
    if (workOrders.length === 0) return;
    const headers = [
      "WorkOrderNumber",
      "MeterNumber",
      "Type",
      "Status",
      "Description",
      "RequestedAt",
      "CompletedAt",
      "TimeTaken",
    ];
    const rows = workOrders.map((item) => {
      return [
        item.workOrderNumber,
        item.meterNumber,
        item.workOrderType,
        item.status,
        item.reason || "N/A",
        formatDate(item.createdAt, "DD/MM/YYYY HH:mm:ss"),
        item.completedDate
          ? formatDate(item.completedDate, "DD/MM/YYYY HH:mm:ss")
          : "N/A",
        calculateTimeTaken(item.createdAt, item.completedDate),
      ].join("\t");
    });
    const excelContent = `${headers.join("\t")}\n${rows.join("\n")}`;

    const blob = new Blob([excelContent], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `WorkOrders_${searchTerm || "All"}.xls`;
    link.click();
    URL.revokeObjectURL(link.href);
    setAnchorEl(null);
  };

  const tableHeaders = COLUMNS.map((col) => (
    <TableSortLabel
      key={col.id}
      active={orderBy === col.id}
      direction={orderBy === col.id ? order : "asc"}
      onClick={() => handleSort(col.id)}
      sx={{
        fontWeight: 600,
        "&.Mui-active": {
          color: "inherit",
        },
        "& .MuiTableSortLabel-icon": {
          color: "inherit !important",
        },
      }}
    >
      {col.label}
    </TableSortLabel>
  ));

  const sortedWorkOrders = useMemo(() => {
    const sorted = [...workOrders];
    sorted.sort((a: any, b: any) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [workOrders, order, orderBy]);

  const rowsContent = sortedWorkOrders?.map((item: WorkOrderV2T) => {
    const {
      label: statusLabel,
      color: statusColor,
      bgColor: statusBg,
    } = getStatusDetails(item.status);
    const {
      label: typeLabel,
      color: typeColor,
      bgColor: typeBg,
    } = getTypeDetails(item.workOrderType);

    return (
      <TableRow key={item.id} sx={{ height: "50px" }}>
        <TableCell>
          <Typography variant="tableBody" fontWeight={600} color="#1064ED">
            {item.workOrderNumber}
          </Typography>
        </TableCell>
        <TableCell>
          <Box
            onClick={() => {
              navigate(`/meter-dashboard?meterid=${item.meterId}`);
            }}
            sx={{
              cursor: "pointer",
              color: "#1064ED",
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            <Typography variant="tableBody" fontWeight={600} color="inherit">
              {item.meterNumber}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={typeLabel}
            size="small"
            sx={{
              backgroundColor: typeBg,
              color: typeColor,
              fontWeight: 600,
              fontSize: "11px",
              border: `1px solid ${typeColor}`,
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              backgroundColor: statusBg,
              color: statusColor,
              fontWeight: 600,
              fontSize: "11px",
              border: `1px solid ${statusColor}`,
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="tableBody">{item.reason || "N/A"}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="tableBody">
            {formatDate(item.createdAt, "DD/MM/YYYY HH:mm:ss")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="tableBody">
            {item.completedDate
              ? formatDate(item.completedDate, "DD/MM/YYYY HH:mm:ss")
              : "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="tableBody">
            {calculateTimeTaken(item.createdAt, item.completedDate)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Layout>
      <CustomToast
        open={toast.message}
        severity={toast.severity as any}
        setToast={setToast}
      />
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Grid container spacing={2} width={"100%"}>
              <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                <CustomSearch
                  sx={{ width: "100%" }}
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchTerm(e.target.value);
                  }}
                  onClick={() => {
                    fetchWorkOrders(0, pagination.pageSize);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchWorkOrders(0, pagination.pageSize);
                    }
                  }}
                  placeholder={"Search by Meter Number / ID"}
                />
              </Grid>
            </Grid>

            <IconButton
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                setAnchorEl(event.currentTarget)
              }
            >
              <Download />
            </IconButton>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <IconButton onClick={downloadCSV}>
                <Typography sx={{ p: 2 }}>Csv</Typography>
              </IconButton>
              <Divider />
              <IconButton onClick={downloadExcel}>
                <Typography sx={{ p: 2 }}>Excel</Typography>
              </IconButton>
            </Popover>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="body1">Work Orders</Typography>}
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <CustomDataTable
            sx={{ maxHeight: "600px" }}
            columns={tableHeaders}
            isLoading={isLoading}
            rowsContent={rowsContent || []}
            totalCount={pagination.totalCount}
            pagination={true}
            rowsPerPage={pagination.pageSize}
            page={pagination.currentPage}
            onPageChange={(_, newPage: number) => {
              setPagination({ ...pagination, currentPage: newPage });
            }}
            onRowsPerPageChange={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              setPagination({
                ...pagination,
                pageSize: parseInt(e.target.value, 10),
                currentPage: 0,
              });
            }}
            Actions={true}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default WorkOrders;
